// Forward → "Maandprijzen": één termijnprijs per leveringsmaand, van deze maand
// tot drie jaar vooruit, voor Power BE Base en TTF Gas Base.
//
// De termijnmarkt noteert niet elke maand apart. Dicht bij vandaag zijn er
// maandcontracten (Nov-26, Dec-26 …), verder weg alleen kwartalen (Q1-27) en
// kalenderjaren (Cal-28). Voor een maand nemen we daarom de fijnste notering die
// bestaat: eerst het maandcontract, anders het kwartaal waar de maand in valt,
// anders het kalenderjaar. Geen van de drie → de cel blijft leeg (dat is o.a. de
// lopende maand: die wordt niet meer als termijnproduct verhandeld).
//
// Als notering geldt altijd de MEEST RECENTE handelsdag in de opgehaalde reeks;
// die datum gaat mee in de bestandsnaam en in een kolom.
//
// Namen die het bronsysteem gebruikt (gezien op echte data, 10-2026):
//   maand      Nov-26        kwartaal  Q1-27        jaar  Cal-27
//   NIET:      Dec-26-Mar-27 (strip), Win-27 / Sum-27 (seizoen) → genegeerd.

export const MAAND_AFK = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const AANTAL_MAANDEN = 36;
export const SOORT_LABEL = { maand: 'Maand', kwartaal: 'Kwartaal', jaar: 'Jaar (Cal)' };

const jaarVol = (yy) => (yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10));

// Vertaalt een contractnaam naar { soort, jaar, maand|kwartaal }. Alles wat geen
// zuiver maand-, kwartaal- of jaarproduct is, levert null.
export function contractSleutel(naam) {
  const s = String(naam ?? '').trim();
  // "Cal-27" past ook op het maandpatroon (drie letters + jaar): eerst Cal toetsen.
  let m = /^Cal-(\d{2}|\d{4})$/.exec(s);
  if (m) return { soort: 'jaar', jaar: jaarVol(m[1]) };
  m = /^Q([1-4])-(\d{2}|\d{4})$/.exec(s);
  if (m) return { soort: 'kwartaal', jaar: jaarVol(m[2]), kwartaal: parseInt(m[1], 10) };
  m = /^([A-Z][a-z]{2})-(\d{2}|\d{4})$/.exec(s);
  if (m) {
    const idx = MAAND_AFK.indexOf(m[1]);
    return idx >= 0 ? { soort: 'maand', jaar: jaarVol(m[2]), maand: idx + 1 } : null;
  }
  return null;
}

const sleutelTekst = (k) => {
  if (k.soort === 'maand') return `M:${k.jaar}-${k.maand}`;
  if (k.soort === 'kwartaal') return `Q:${k.jaar}-${k.kwartaal}`;
  return `C:${k.jaar}`;
};

// De meest recente handelsdag met minstens één bruikbare prijs, als opzoektabel.
// `payload` is het antwoord van /api/lynx/forward (tradeDates chronologisch).
// Geeft null als er niets te noteren valt.
export function laatsteNotering(payload) {
  const dagen = payload?.tradeDates || [];
  for (let i = dagen.length - 1; i >= 0; i--) {
    const td = dagen[i];
    const prijzen = new Map();
    for (const p of td.products || []) {
      const k = contractSleutel(p.name);
      const v = Number(p.value);
      if (!k || !Number.isFinite(v)) continue;
      prijzen.set(sleutelTekst(k), { waarde: v, contract: p.name, soort: k.soort });
    }
    if (prijzen.size) return { datum: td.tradeDateLabel || String(td.tradeDate || '').slice(0, 10), prijzen };
  }
  return null;
}

// Prijs voor één leveringsmaand: maand → kwartaal → jaar, of null.
export function prijsVoorMaand(notering, jaar, maand) {
  if (!notering) return null;
  const kwartaal = Math.ceil(maand / 3);
  return notering.prijzen.get(`M:${jaar}-${maand}`)
    || notering.prijzen.get(`Q:${jaar}-${kwartaal}`)
    || notering.prijzen.get(`C:${jaar}`)
    || null;
}

const BRUSSEL_MAAND = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit' });

// De lopende maand (Brusselse kalender) en de volgende, `aantal` in totaal.
export function maandenVanaf(nu = new Date(), aantal = AANTAL_MAANDEN) {
  const [j, m] = BRUSSEL_MAAND.format(nu).split('-').map(Number);
  const uit = [];
  for (let i = 0; i < aantal; i++) {
    const idx = (m - 1) + i;
    uit.push({ jaar: j + Math.floor(idx / 12), maand: (idx % 12) + 1 });
  }
  return uit;
}

export const periodeLabel = ({ jaar, maand }) => `${jaar}-${String(maand).padStart(2, '0')}`;

// ─── CALC: een kwartaal- of Cal-prijs verdelen over de maanden ───────────────
// Een maand zonder eigen contract krijgt in de CALC-kolom niet de platte
// kwartaal- of jaarprijs, maar die prijs verdeeld volgens het profiel van de
// laatst gekende maandprijzen: voor elke maand van de periode de meest recente
// gekende prijs van diezelfde kalendermaand uit een eerder jaar (voor Q2-27 dus
// Apr-26, May-26, Jun-26; voor Cal-28 de twaalf maanden van 2027 waar die
// bekend zijn, anders 2026). CALC = periodeprijs × profiel(maand) / gemiddelde
// van het profiel over de periode, zodat het gemiddelde van de maanden precies
// de periodeprijs blijft. Voorbeeld: kwartaal 50, profiel 80/100/120 → 40/50/60.
// Dagen per maand tellen niet mee. Ontbreekt een profielmaand, dan blijft de
// periodeprijs staan en zegt de basis-kolom dat.

// Profiel: Map 'jaar-maand' → { waarde, datum }, uit het maandprofiel van de
// server (laatst gekende prijs + datum per maandcontract) én uit de laatste
// notering (de lopende maandcontracten zijn óók "laatst gekend"; die zijn verser).
export function bouwProfiel(maandprofiel, notering) {
  const profiel = new Map();
  for (const [naam, info] of Object.entries(maandprofiel?.maanden || {})) {
    const k = contractSleutel(naam);
    const w = Number(info?.waarde);
    if (k?.soort === 'maand' && Number.isFinite(w)) profiel.set(`${k.jaar}-${k.maand}`, { waarde: w, datum: String(info.datum || '') });
  }
  for (const p of notering?.prijzen.values() || []) {
    if (p.soort !== 'maand') continue;
    const k = contractSleutel(p.contract);
    profiel.set(`${k.jaar}-${k.maand}`, { waarde: p.waarde, datum: String(notering.datum || '') });
  }
  return profiel;
}

// De laatst gekende prijs van kalendermaand `maand` uit een jaar vóór `jaar`:
// van alle kandidaten (tot vijf jaar terug) die met de MEEST RECENTE notering.
// Niet simpelweg het hoogste jaar: het bronsysteem noteerde ooit verre maanden
// (Jan-28 in juni 2026, Dec-27 in augustus) die daarna uit de notering vielen;
// Jan-27 van vandaag zegt meer over het januari-profiel dan Jan-28 van maanden
// geleden. Bij gelijke datum wint het hoogste jaar.
export function profielWaarde(profiel, jaar, maand) {
  let beste = null;
  for (let j = jaar - 1; j >= jaar - 5; j--) {
    const p = profiel.get(`${j}-${maand}`);
    if (!p) continue;
    if (!beste || p.datum > beste.datum) beste = { waarde: p.waarde, datum: p.datum, jaar: j, naam: `${MAAND_AFK[maand - 1]}-${String(j).slice(-2)}` };
  }
  return beste;
}

const rond3 = (x) => Math.round(x * 1000) / 1000;

export function calcVoorMaand(prijs, jaar, maand, profiel) {
  if (!prijs) return null;
  if (prijs.soort === 'maand') return { waarde: prijs.waarde, basis: '' };
  const kwartaal = Math.ceil(maand / 3);
  const periode = prijs.soort === 'kwartaal'
    ? [1, 2, 3].map((i) => (kwartaal - 1) * 3 + i)
    : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const pw = periode.map((m) => (profiel ? profielWaarde(profiel, jaar, m) : null));
  if (pw.some((x) => !x)) return { waarde: prijs.waarde, basis: `${prijs.contract} ongewijzigd: geen volledig maandprofiel` };
  const gemiddelde = pw.reduce((s, x) => s + x.waarde, 0) / pw.length;
  const eigen = pw[periode.indexOf(maand)];
  if (!(gemiddelde > 0)) return { waarde: prijs.waarde, basis: `${prijs.contract} ongewijzigd: profiel zonder gemiddelde` };
  return {
    waarde: rond3((prijs.waarde * eigen.waarde) / gemiddelde),
    basis: `${prijs.contract} verdeeld volgens ${pw.map((x) => x.naam).join(', ')}`,
  };
}

// Eén rij per maand, met per product de gekozen prijs (of null) en de CALC-prijs.
// `producten` = [{ id, notering, profiel? }] in kolomvolgorde.
export function bouwMaandprijzen(producten, nu = new Date(), aantal = AANTAL_MAANDEN) {
  return maandenVanaf(nu, aantal).map((mnd) => {
    const rij = { periode: periodeLabel(mnd), jaar: mnd.jaar, maand: mnd.maand, prijzen: {}, calc: {} };
    for (const p of producten) {
      rij.prijzen[p.id] = prijsVoorMaand(p.notering, mnd.jaar, mnd.maand);
      rij.calc[p.id] = calcVoorMaand(rij.prijzen[p.id], mnd.jaar, mnd.maand, p.profiel);
    }
    return rij;
  });
}

// Historische maanden (vóór de maand van vandaag) voor de tabel op de Forward-
// pagina: per maand de LAATST gekende prijs van haar eigen maandcontract, uit het
// profiel (verlopen contracten uit het venster van de server + de live notering).
// Zelfde rijvorm als bouwMaandprijzen, met `historisch: true` en per product de
// eigen noteringsdatum (een verlopen contract noteerde voor het laatst vlak vóór
// zijn maand begon). Maanden waarvoor geen enkel product een prijs heeft, vallen
// weg — de lijst loopt dus terug tot waar het venster van de server reikt.
export function historischeMaandprijzen(producten, nu = new Date(), aantalTerug = 24) {
  const [j, m] = BRUSSEL_MAAND.format(nu).split('-').map(Number);
  const uit = [];
  for (let i = aantalTerug; i >= 1; i--) {
    const idx = (m - 1) - i;
    const jaar = j + Math.floor(idx / 12), maand = ((idx % 12) + 12) % 12 + 1;
    const rij = { periode: periodeLabel({ jaar, maand }), jaar, maand, historisch: true, prijzen: {}, calc: {} };
    let iets = false;
    for (const p of producten) {
      const h = p.profiel ? p.profiel.get(`${jaar}-${maand}`) : null;
      if (h) {
        iets = true;
        rij.prijzen[p.id] = { waarde: h.waarde, contract: `${MAAND_AFK[maand - 1]}-${String(jaar).slice(2)}`, soort: 'maand', notering: h.datum };
        rij.calc[p.id] = { waarde: h.waarde, basis: '' };
      } else { rij.prijzen[p.id] = null; rij.calc[p.id] = null; }
    }
    if (iets) uit.push(rij);
  }
  return uit;
}

// Kolommen voor exportRowsToCsv: Maand, dan per product prijs / CALC / contract /
// soort / noteringsdatum / CALC-basis.
export function maandprijzenKolommen(producten) {
  const kolommen = [{ header: 'Maand', get: (r) => r.periode }];
  for (const p of producten) {
    kolommen.push(
      { header: `${p.label} (EUR/MWh)`, get: (r) => r.prijzen[p.id]?.waarde ?? null },
      { header: `${p.label} CALC (EUR/MWh)`, get: (r) => r.calc[p.id]?.waarde ?? null },
      { header: `${p.label} contract`, get: (r) => r.prijzen[p.id]?.contract ?? '' },
      { header: `${p.label} type`, get: (r) => (r.prijzen[p.id] ? SOORT_LABEL[r.prijzen[p.id].soort] : '') },
      { header: `${p.label} notering`, get: (r) => (r.prijzen[p.id] ? p.notering?.datum ?? '' : '') },
      { header: `${p.label} CALC basis`, get: (r) => r.calc[p.id]?.basis ?? '' },
    );
  }
  return kolommen;
}

// ─── Grafiek: reeksvolgorde en de "ladder" ──────────────────────────────────
// Eerste en laatste leveringsmaand van een contract, als maandnummer sinds jaar 0
// (jaar × 12 + maand), zodat "ligt na" een gewone vergelijking is.
const maandKey = (jaar, maand) => jaar * 12 + maand;
export function contractBereik(naam) {
  const k = contractSleutel(naam);
  if (!k) return null;
  if (k.soort === 'maand') return { van: maandKey(k.jaar, k.maand), tot: maandKey(k.jaar, k.maand) };
  if (k.soort === 'kwartaal') return { van: maandKey(k.jaar, (k.kwartaal - 1) * 3 + 1), tot: maandKey(k.jaar, k.kwartaal * 3) };
  return { van: maandKey(k.jaar, 1), tot: maandKey(k.jaar, 12) };
}

// Alle zuivere contracten uit een reeksenlijst, per soort chronologisch:
// eerst de maanden, dan de kwartalen, dan de Cal's. Seizoenen/strips vallen weg.
export function sorteerContracten(namen) {
  const per = { maand: [], kwartaal: [], jaar: [] };
  for (const n of namen || []) {
    const k = contractSleutel(n);
    if (k) per[k.soort].push({ naam: n, van: contractBereik(n).van });
  }
  const chrono = (l) => l.sort((a, b) => a.van - b.van).map((x) => x.naam);
  return [...chrono(per.maand), ...chrono(per.kwartaal), ...chrono(per.jaar)];
}

// De ladder die de grafiek toont: de eerste `maanden` maandcontracten, dan de
// `kwartalen` kwartalen die NA de laatste gekozen maand beginnen, dan elke Cal
// die NA het laatste gekozen contract begint. Eén regel dus: elk volgend
// contract begint na het vorige, zodat niets dubbel getekend wordt.
// Standaard 3 + 3: Nov-26, Dec-26, Jan-27, Q2-27, Q3-27, Q4-27, Cal-28, Cal-29.
// Zo blijft de horizon leesbaar zonder tientallen knoppen.
export const LADDER_STANDAARD = { maanden: 3, kwartalen: 3 };
export const LADDER_MAAND_OPTIES = [0, 1, 2, 3, 6, 12];
export const LADDER_KWARTAAL_OPTIES = [0, 1, 2, 3, 4, 8];

// De ladder start bij de maand van vandaag (Brussel): de grafiek dekt 30 tot
// 365 dagen terug en daar zitten ook contracten in die intussen verlopen zijn
// (in oktober nog Sep-26); die horen niet vooraan in de ladder.
export function forwardLadder(namen, { maanden = 3, kwartalen = 3, nu = new Date() } = {}) {
  const [nuJaar, nuMaand] = BRUSSEL_MAAND.format(nu).split('-').map(Number);
  const vanaf = maandKey(nuJaar, nuMaand);
  const alle = sorteerContracten(namen).filter((n) => contractBereik(n).van >= vanaf);
  const soortVan = (n) => contractSleutel(n).soort;
  const maandNamen = alle.filter((n) => soortVan(n) === 'maand').slice(0, Math.max(0, maanden));
  let totNu = maandNamen.length ? contractBereik(maandNamen[maandNamen.length - 1]).tot : null;
  const kwartaalNamen = alle
    .filter((n) => soortVan(n) === 'kwartaal' && (totNu == null || contractBereik(n).van > totNu))
    .slice(0, Math.max(0, kwartalen));
  if (kwartaalNamen.length) totNu = contractBereik(kwartaalNamen[kwartaalNamen.length - 1]).tot;
  const calNamen = alle.filter((n) => soortVan(n) === 'jaar' && (totNu == null || contractBereik(n).van > totNu));
  return [...maandNamen, ...kwartaalNamen, ...calNamen];
}

// Bestandsnaam met de noteringsdatum erin. Eén datum als alle producten op
// dezelfde handelsdag noteren, anders de datum per product.
export function maandprijzenBestandsnaam(producten, basis = 'forward-maandprijzen') {
  const met = producten.filter((p) => p.notering?.datum);
  if (!met.length) return `${basis}.csv`;
  const datums = [...new Set(met.map((p) => p.notering.datum))];
  if (datums.length === 1) return `${basis}_notering-${datums[0]}.csv`;
  return `${basis}_notering-${met.map((p) => `${p.kort}-${p.notering.datum}`).join('_')}.csv`;
}
